import React, { ReactNode, useId } from "react";
import styled from "styled-components";
import Tooltip from "common/Tooltips/Tooltip";
import { SPARKLINE_DOT_ID, SPARKLINE_LINE_ID } from "testIds";
import { monotonePath, Point } from "common/NewLook/monotonePath.ts";

export interface SparklineLevel {
  key: string;
  value: number;
  color: string;
  /** Short tag shown in the right gutter, e.g. "+0.31%". */
  label: ReactNode;
  /** Tooltip over the tag, e.g. "Next Take-Profit". */
  tooltip?: ReactNode;
}

interface NewLookSparklineProps {
  values: number[];
  levels?: SparklineLevel[];
  height?: number;
  /** Width of the right gutter that holds the level tags. */
  gutter?: number;
  /** Line colour; the new look's blue by default. */
  color?: string;
  /** Marks the last point with a dot. */
  showDot?: boolean;
}

const PAD = 5;

/**
 * A quiet price sparkline for the new look's trade cards: the price line with
 * a soft fill, dashed lines for the next orders and their distance as small
 * tags in a right gutter. Renders an empty placeholder of the same height
 * while there is no data, so the card doesn't jump.
 */
const NewLookSparkline = ({
  values,
  levels = [],
  height = 56,
  gutter = 56,
  color,
  showDot = true,
}: NewLookSparklineProps) => {
  const id = useId().replace(/:/g, "");
  const valid = values.filter((value) => Number.isFinite(value));
  const hasLine = valid.length > 1;
  const withLevels = hasLine ? levels : [];

  const all = [...valid, ...withLevels.map((level) => level.value)];
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || Math.abs(max) || 1;
  const toY = (value: number) =>
    PAD + (1 - (value - min) / range) * (height - PAD * 2);

  const points = valid.map(
    (value, index): Point => [
      (index / Math.max(valid.length - 1, 1)) * 100,
      toY(value),
    ]
  );
  const line = hasLine ? monotonePath(points) : "";
  const area = hasLine ? `M0,${height} L${line.slice(1)} L100,${height} Z` : "";
  const lastY = hasLine ? points[points.length - 1][1] : height / 2;

  // Keep tags from overlapping: nudge them apart from top to bottom.
  const tags = withLevels
    .map((level) => ({ ...level, y: toY(level.value) }))
    .sort((a, b) => a.y - b.y);
  for (let i = 1; i < tags.length; i++) {
    if (tags[i].y - tags[i - 1].y < TAG_HEIGHT)
      tags[i].y = tags[i - 1].y + TAG_HEIGHT;
  }

  return (
    <Wrapper style={{ height }} $gutter={withLevels.length ? gutter : 0}>
      <Plot>
        {hasLine ? (
          <>
            <Svg
              viewBox={`0 0 100 ${height}`}
              preserveAspectRatio="none"
              style={{ height, color }}
            >
              <defs>
                <linearGradient id={`spark-${id}`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopOpacity={0.16} />
                  <stop offset="100%" stopOpacity={0} />
                </linearGradient>
              </defs>
              {withLevels.map((level) => (
                <line
                  key={level.key}
                  x1={0}
                  x2={100}
                  y1={toY(level.value)}
                  y2={toY(level.value)}
                  stroke={level.color}
                  strokeDasharray="3 3"
                  strokeOpacity={0.75}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              <Area d={area} fill={`url(#spark-${id})`} />
              <Line
                data-testid={SPARKLINE_LINE_ID}
                d={line}
                vectorEffect="non-scaling-stroke"
              />
            </Svg>
            {showDot && (
              <Dot
                data-testid={SPARKLINE_DOT_ID}
                style={{ top: lastY, backgroundColor: color }}
              />
            )}
          </>
        ) : (
          <Baseline />
        )}
      </Plot>
      {!!withLevels.length && (
        <Gutter>
          {tags.map((tag) => (
            <Tooltip key={tag.key} title={tag.tooltip} placement="left">
              <Tag
                style={{
                  top: Math.min(Math.max(tag.y, TAG_HEIGHT / 2), height),
                  color: tag.color,
                }}
              >
                {tag.label}
              </Tag>
            </Tooltip>
          ))}
        </Gutter>
      )}
    </Wrapper>
  );
};

export default React.memo(NewLookSparkline);

const TAG_HEIGHT = 16;

const Wrapper = styled.div<{ $gutter: number }>`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${(p) => p.$gutter}px;
  column-gap: 6px;
`;
const Plot = styled.div`
  position: relative;
  min-width: 0;
`;
const Svg = styled.svg`
  display: block;
  width: 100%;
  overflow: visible;
  color: ${(p) => p.theme.colors.primaryBlue};
  stop {
    stop-color: currentColor;
  }
`;
const Area = styled.path``;
const Line = styled.path`
  fill: none;
  stroke: currentColor;
  stroke-width: 1.75;
  stroke-linecap: round;
  stroke-linejoin: round;
`;
const Dot = styled.span`
  position: absolute;
  left: 100%;
  width: 7px;
  height: 7px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: ${(p) => p.theme.colors.primaryBlue};
`;
const Baseline = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  border-top: 1px dashed ${(p) => p.theme.colors.cardBorderColor};
`;
const Gutter = styled.div`
  position: relative;
`;
const Tag = styled.span`
  position: absolute;
  right: 0;
  transform: translateY(-50%);
  height: ${TAG_HEIGHT}px;
  line-height: ${TAG_HEIGHT}px;
  font-size: 11px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  cursor: default;
`;
